// navigationHandling.js
import {getCSSVariableValue} from "./smooth-scrolling.js";

const NAVIGATION_SELECTORS = {
    MAIN_NAV: '#mainNav',
    MOBILE_NAV: '#mobileNav',
    ONEPAGE_NAV: '.onepagenavi--wrapper'
};

class NavigationManager {
    constructor() {
        this.links = [];
        this.linkMap = new Map();
        this.visibleSections = new Map();
        this.sectionObserver = null;
        this.scrollOffset = 0;
        this.resizeObserver = null;
        this.manualOverride = null;
        this.currentActiveSection = null;

        this.handleIntersections = this.handleIntersections.bind(this);
        this.recalculate = this.recalculate.bind(this);
        this.handleInitialState = this.handleInitialState.bind(this);
        this.handleManualActivation = this.handleManualActivation.bind(this);

        const init = () => {
            this.recalculate();
            window.addEventListener('resize', () => {
                window.requestAnimationFrame(this.recalculate);
            }, {passive: true});
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init, {once: true});
        } else {
            init();
        }
    }

    initializeLinks() {
        this.links = [];
        this.linkMap.clear();

        const selectors = [
            `${NAVIGATION_SELECTORS.MAIN_NAV} a[href*="#"]:not(.invisible)`,
            `${NAVIGATION_SELECTORS.MOBILE_NAV} a[href*="#"]:not(.invisible)`,
            `${NAVIGATION_SELECTORS.ONEPAGE_NAV} a`,
            '#articleNav a[href^="#"]',
            '#stoerer-130-1 .article-nav-content a[href^="#"]'
        ];

        selectors.forEach(selector => {
            document.querySelectorAll(selector).forEach(link => {
                const href = link.getAttribute('href');
                if (!href || !href.includes('#')) {
                    return;
                }

                const sectionId = href.split('#').pop();
                if (!sectionId) {
                    return;
                }

                this.links.push(link);
                if (!this.linkMap.has(sectionId)) {
                    this.linkMap.set(sectionId, new Set());
                }
                this.linkMap.get(sectionId).add(link);
            });
        });

        return Array.from(this.linkMap.keys());
    }

    setActiveLink(sectionId) {
        const normalizedId = typeof sectionId === 'string' ? sectionId.replace(/^#/, '') : '';

        if (!normalizedId) {
            this.clearActiveLinks(true);
            return;
        }

        const activeLinks = this.linkMap.get(normalizedId) || new Set();

        if (activeLinks.size === 0) {
            const targetElement = document.getElementById(normalizedId);
            if (!targetElement) {
                this.clearActiveLinks(true);
                return;
            }
            this.currentActiveSection = normalizedId;
            return;
        }

        this.clearActiveLinks();

        activeLinks.forEach(link => {
            link.classList.remove('sibling');
            link.classList.add('active');
            const parentLi = link.closest('li');
            if (parentLi) {
                parentLi.classList.remove('sibling');
                parentLi.classList.add('active');
            }
        });

        this.currentActiveSection = normalizedId;
    }

    clearActiveLinks(resetCurrent = false) {
        document.querySelectorAll('li.active, a.active').forEach(el => {
            el.classList.remove('active');
            if (el.tagName.toLowerCase() === 'li') {
                el.classList.add('sibling');
            }
        });

        if (resetCurrent) {
            this.currentActiveSection = null;
        }
    }

    handleIntersections(entries) {
        entries.forEach(entry => {
            const {target, isIntersecting, intersectionRatio} = entry;
            const sectionId = target.id;
            if (!sectionId) {
                return;
            }

            if (isIntersecting) {
                this.visibleSections.set(sectionId, {
                    ratio: intersectionRatio,
                    top: entry.boundingClientRect.top
                });
            } else {
                this.visibleSections.delete(sectionId);
            }
        });

        if (this.manualOverride && this.manualOverride.sectionId) {
            const now = typeof performance !== 'undefined' && typeof performance.now === 'function'
                ? performance.now()
                : Date.now();
            if (now < this.manualOverride.expires) {
                return;
            }
            this.manualOverride = null;
        }

        if (this.visibleSections.size === 0) {
            if (!this.currentActiveSection) {
                this.handleInitialState();
            }
            return;
        }

        const bestSection = Array.from(this.visibleSections.entries())
            .sort((a, b) => {
                const topDiff = a[1].top - b[1].top;
                if (Math.abs(topDiff) > 1) {
                    return topDiff;
                }
                return b[1].ratio - a[1].ratio;
            })[0]?.[0];

        if (bestSection) {
            this.setActiveLink(bestSection);
        }
    }

    findLastValidLink() {
        for (let i = this.links.length - 1; i >= 0; i--) {
            const link = this.links[i];
            const href = link.getAttribute('href');
            const sectionId = href.split('#').pop();
            if (document.getElementById(sectionId)) {
                return link;
            }
        }
        return null;
    }

    handleInitialState() {
        const hash = window.location.hash;
        if (hash) {
            const sectionId = hash.substring(1);
            if (document.getElementById(sectionId)) {
                this.setActiveLink(sectionId);
                return;
            }
        }

        const firstLink = this.links[0];
        if (firstLink) {
            const href = firstLink.getAttribute('href');
            const sectionId = href.split('#').pop();
            if (document.getElementById(sectionId)) {
                this.setActiveLink(sectionId);
            }
        }
    }

    recalculate() {
        const sectionIds = this.initializeLinks();
        this.scrollOffset = getCSSVariableValue('--bs-scrolloffset');
        this.visibleSections.clear();

        if (this.sectionObserver) {
            this.sectionObserver.disconnect();
        }

        if (!('IntersectionObserver' in window)) {
            this.handleInitialState();
            return;
        }

        this.sectionObserver = new IntersectionObserver(this.handleIntersections, {
            rootMargin: `${-this.scrollOffset}px 0px -65% 0px`,
            threshold: [0, 0.2, 0.4, 0.6, 0.8, 1]
        });

        sectionIds.forEach(sectionId => {
            const section = document.getElementById(sectionId);
            if (section) {
                this.sectionObserver.observe(section);
            }
        });

        this.handleInitialState();
    }

    handleManualActivation(sectionId) {
        if (!sectionId) {
            return;
        }

        const normalizedId = sectionId.replace(/^#/, '');
        const now = typeof performance !== 'undefined' && typeof performance.now === 'function'
            ? performance.now()
            : Date.now();

        this.manualOverride = {
            sectionId: normalizedId,
            expires: now + 1200,
        };

        this.setActiveLink(normalizedId);
    }
}

// Erstelle eine Instanz
const navigationManager = new NavigationManager();

// Exportiere die benötigten Funktionen
export function changeAnchorLinks() {
    navigationManager.recalculate();
}

export function changeNavLinksAfterLoad() {
    navigationManager.handleInitialState();
}

// Mache die Funktionen auch global verfügbar
window.changeAnchorLinks = changeAnchorLinks;
window.changeNavLinksAfterLoad = changeNavLinksAfterLoad;

// Event Listener
document.addEventListener('DOMContentLoaded', () => {
    navigationManager.handleInitialState();
});

// Event Listener für dynamisch generierte Artikel-Navigation
document.addEventListener('articleNavGenerated', () => {
    navigationManager.recalculate();
});

document.addEventListener('smooth-scroll:start', (event) => {
    const targetId = event?.detail?.targetId;
    if (!targetId) {
        return;
    }
    navigationManager.handleManualActivation(targetId);
});
